/**
 * Date formatting utilities
 * Pure functions for date/time formatting
 */

/**
 * Calculate age based on ISO date string (example: "1985-10-25")
 * @param {string | Date | null | undefined} born - Birth date as ISO string or Date object
 */
export const calculateAge = (born: string | Date | null | undefined): number | null => {
  if (!born) return null;

  const birthDate = typeof born === "string" ? new Date(born) : born;
  if (isNaN(birthDate.getTime())) return null;

  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();

  const monthDiff = today.getMonth() - birthDate.getMonth();
  const dayDiff = today.getDate() - birthDate.getDate();

  if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
    age--;
  }

  return age;
};

/**
 * Format date in localized long format (example: "20 март 2025 г.")
 * @param {string | Date | null | undefined} dateInput - Date input as ISO string or Date object
 * @param {string} locale - Optional locale override (defaults to current i18n locale)
 */
export const formatDate = (dateInput: string | Date | null | undefined, locale?: string): string => {
  if (!dateInput) return "";

  try {
    const date = typeof dateInput === "string" ? new Date(dateInput) : dateInput;

    if (isNaN(date.getTime())) return "";

    // Use provided locale or get from i18n
    const currentLocale = locale ?? (useNuxtApp().$i18n as { locale: { value: string } }).locale.value;

    return new Intl.DateTimeFormat(currentLocale, {
      day: "numeric",
      month: "long",
      year: "numeric",
    }).format(date);
  } catch (error) {
    console.error("Error formatting date:", error);
    return "";
  }
};

/**
 * Month abbreviations for each locale
 */
const MONTH_ABBREVIATIONS: Record<string, string[]> = {
  bg: ["яну", "фев", "мар", "апр", "май", "юни", "юли", "авг", "сеп", "окт", "ное", "дек"],
  mk: ["јан", "фев", "мар", "апр", "мај", "јун", "јул", "авг", "сеп", "окт", "ное", "дек"],
  en: ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"],
};

/**
 * Format date and time separately in localized format { date: "20 мар 2025", time: "14:30" }
 * If the date is today, returns only the time (empty date string)
 * @param {string | Date | null | undefined} dateInput - Date input as ISO string or Date object
 * @param {string} locale - Optional locale override (defaults to current i18n locale)
 */
export const formatDateTime = (
  dateInput: string | Date | null | undefined,
  locale?: string,
): { date: string; time: string } => {
  if (!dateInput) return { date: "", time: "" };

  try {
    const date = typeof dateInput === "string" ? new Date(dateInput) : dateInput;

    if (isNaN(date.getTime())) return { date: "", time: "" };

    // Use provided locale or get from i18n
    const currentLocale = locale ?? (useNuxtApp().$i18n as { locale: { value: string } }).locale.value;

    // Check if the date is today
    const today = new Date();
    const isToday =
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear();

    // Format time based on locale
    // Bulgarian and Macedonian use 24-hour format, English uses 12-hour with AM/PM
    const use24HourFormat = currentLocale === "bg" || currentLocale === "mk";
    const timeStr = new Intl.DateTimeFormat(currentLocale, {
      hour: "2-digit",
      minute: "2-digit",
      hour12: !use24HourFormat, // false for bg/mk (24-hour), true for en (12-hour with AM/PM)
    }).format(date);

    // If today, return only time
    if (isToday) {
      return { date: "", time: timeStr };
    }

    // Format date manually: 2-digit day, 3-letter month, 4-digit year
    const day = date.getDate().toString().padStart(2, "0");
    const monthIndex = date.getMonth();
    const monthAbbr = MONTH_ABBREVIATIONS[currentLocale]?.[monthIndex] || MONTH_ABBREVIATIONS.en?.[monthIndex] || "";
    const year = date.getFullYear();

    const dateStr = `${day} ${monthAbbr} ${year}`;

    return { date: dateStr, time: timeStr };
  } catch (error) {
    console.error("Error formatting datetime:", error);
    return { date: "", time: "" };
  }
};

/**
 * Check if a date is today
 * @param {string | Date | null | undefined} dateInput - Date input as ISO string or Date object
 */
export const isToday = (dateInput: string | Date | null | undefined): boolean => {
  if (!dateInput) return false;

  try {
    const date = typeof dateInput === "string" ? new Date(dateInput) : dateInput;

    if (isNaN(date.getTime())) return false;

    const today = new Date();
    return (
      date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
    );
  } catch (error) {
    return false;
  }
};
